// DeleteInterviewModal.jsx
import React from "react"
import { X, AlertTriangle, Trash2, Calendar } from "lucide-react"

const DeleteInterviewModal = ({
  isOpen,
  onClose,
  interview,
  interviewGroup,
  onDeleteSingle,
  onDeleteSeries,
  isDeleting,
}) => {
  if (!isOpen) return null

  const hasMultipleRounds = interviewGroup && interviewGroup.length > 1
  const isLatestRound =
    interview &&
    interviewGroup &&
    interview.roundNumber ===
      Math.max(...interviewGroup.map((i) => i.roundNumber || 1))

  const candidateName =
    interview?.candidate || interview?.candidateName || "Unknown Candidate"

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Delete Interview
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            disabled={isDeleting}
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-6">
            What would you like to delete for{" "}
            <span className="font-semibold text-gray-900">{candidateName}</span>
            ?
          </p>

          <div className="space-y-3">
            {/* Delete Single Interview Option */}
            <button
              onClick={() => onDeleteSingle(interview)}
              disabled={isDeleting}
              className="w-full p-4 text-left border border-gray-200 rounded-lg hover:border-red-300 hover:bg-red-50 transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-orange-100 rounded-full group-hover:bg-orange-200 transition-colors">
                  <Trash2 className="w-4 h-4 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">
                    Delete This Round Only
                  </h4>
                  <p className="text-sm text-gray-600">
                    {isLatestRound
                      ? `Delete Round ${interview.roundNumber || 1} (Latest)`
                      : `Delete Round ${interview.roundNumber || 1}`}
                  </p>
                  {hasMultipleRounds && (
                    <p className="text-xs text-gray-500 mt-1">
                      Other interview rounds will remain unchanged
                    </p>
                  )}
                </div>
              </div>
            </button>

            {/* Delete Entire Series Option - Only show if multiple rounds exist */}
            {hasMultipleRounds && (
              <button
                onClick={() => onDeleteSeries(interviewGroup)}
                disabled={isDeleting}
                className="w-full p-4 text-left border border-gray-200 rounded-lg hover:border-red-300 hover:bg-red-50 transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-red-100 rounded-full group-hover:bg-red-200 transition-colors">
                    <Calendar className="w-4 h-4 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-1">
                      Delete Entire Interview Series
                    </h4>
                    <p className="text-sm text-gray-600">
                      Delete all {interviewGroup.length} rounds for this
                      candidate
                    </p>
                    <p className="text-xs text-red-500 mt-1 font-medium">
                      ⚠️ This action cannot be undone
                    </p>
                  </div>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>

        {/* Loading Overlay */}
        {isDeleting && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-xl">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
              <span className="text-sm text-gray-600">Deleting...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DeleteInterviewModal
